import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Star, Loader2, MessageSquarePlus, BadgeCheck } from "lucide-react";

const Stars = ({ value, size = "w-4 h-4", onSelect }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type={onSelect ? "button" : undefined}
        disabled={!onSelect}
        onClick={onSelect ? () => onSelect(n) : undefined}
        data-testid={onSelect ? `review-star-${n}` : undefined}
        className={onSelect ? "transition-transform hover:scale-110" : "cursor-default"}
        aria-label={`${n} star`}
      >
        <Star className={`${size} ${n <= value ? "fill-primary text-primary" : "text-white/25"}`} />
      </button>
    ))}
  </span>
);

export default function ProductReviews({ productId }) {
  const { user } = useAuth();
  const [data, setData] = useState({ average: 0, count: 0, reviews: [] });
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);

  const load = () => {
    api.get(`/products/${productId}/reviews`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { setLoading(true); load(); }, [productId]);

  const myReview = user && data.reviews.find((r) => r.customer_id === user.id);

  const submit = async () => {
    if (rating < 1) return toast.error("Please pick a star rating.");
    setSubmitting(true);
    try {
      await api.post(`/products/${productId}/reviews`, { rating, comment: comment.trim() });
      toast.success("Thanks for your review!");
      setOpen(false); setComment(""); setRating(0);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit your review.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="product-reviews" className="mt-16 border-t border-white/10 pt-12">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-anton text-3xl uppercase tracking-tight text-white">Reviews</h2>
          <div className="flex items-center gap-3 mt-2">
            <Stars value={Math.round(data.average)} size="w-5 h-5" />
            <span data-testid="reviews-summary" className="text-white/60 text-sm">
              {data.count ? `${data.average} · ${data.count} review${data.count > 1 ? "s" : ""}` : "No reviews yet"}
            </span>
          </div>
        </div>
        {user ? (
          <button
            data-testid="write-review-button"
            onClick={() => { setOpen((o) => !o); if (myReview) { setRating(myReview.rating); setComment(myReview.comment); } }}
            className="flex items-center gap-2 bg-primary text-black hover:bg-white px-6 py-3 font-bold uppercase tracking-[0.2em] text-xs transition-colors active:scale-95"
          >
            <MessageSquarePlus className="w-4 h-4" /> {myReview ? "Edit your review" : "Write a review"}
          </button>
        ) : (
          <Link to="/login" state={{ from: window.location.pathname }} data-testid="review-login-link" className="text-primary hover:text-white font-bold uppercase tracking-widest text-xs">
            Log in to review
          </Link>
        )}
      </div>

      {open && user && (
        <div data-testid="review-form" className="mt-6 border border-white/15 p-5 space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-widest text-white/50">Your rating</span>
            <Stars value={rating} size="w-7 h-7" onSelect={setRating} />
          </div>
          <textarea
            data-testid="review-comment-input"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Tell others what you think — fit, quality, print…"
            rows={4}
            className="w-full bg-transparent border border-white/20 focus:border-primary outline-none p-3 text-white text-sm resize-none"
          />
          <div className="flex gap-3">
            <button
              data-testid="review-submit-button"
              onClick={submit}
              disabled={submitting}
              className="flex items-center justify-center gap-2 bg-primary text-black hover:bg-white disabled:opacity-40 px-6 py-3 font-bold uppercase tracking-widest text-xs transition-colors"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Submit review
            </button>
            <button onClick={() => setOpen(false)} className="px-6 py-3 border border-white/20 text-white/60 hover:text-white font-bold uppercase tracking-widest text-xs">Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-8 space-y-6">
        {loading ? (
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
        ) : data.reviews.length === 0 ? (
          <p className="text-white/40 text-sm">Be the first to review this jersey.</p>
        ) : (
          data.reviews.map((r) => (
            <div key={r.id} data-testid={`review-${r.id}`} className="border-b border-white/10 pb-6">
              <div className="flex items-center justify-between">
                <Stars value={r.rating} />
                <span className="text-xs text-white/40">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</span>
              </div>
              <p className="text-white font-bold text-sm mt-2 flex items-center gap-2">
                {r.customer_name}
                {r.verified && (
                  <span data-testid={`verified-badge-${r.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-primary border border-primary/40 px-1.5 py-0.5">
                    <BadgeCheck className="w-3 h-3" /> Verified purchase
                  </span>
                )}
              </p>
              {r.comment && <p className="text-white/70 text-sm mt-1">{r.comment}</p>}
            </div>
          ))
        )}
      </div>
    </section>
  );
}
